import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Check, Image, Link, Loader2, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { compressImageFile } from "../lib/image-compress";
import { PROFILE_PICTURE_MAX_DIM } from "../lib/profile-picture";

/**
 * Fenêtre « Changer la photo » — celle de « Mon profil », sortie d'`AccountPage` pour être
 * partagée avec la fiche collaborateur (18/09/2026). Mêmes trois voies : fichier de
 * l'appareil, appareil photo (mobile), lien https://.
 *
 * Correctif au passage : l'image choisie est RÉDUITE dans le navigateur avant envoi
 * (`compressImageFile`, 256 px). Auparavant le fichier partait brut en data URL, que le
 * serveur refusait (« HTTPS requis ») : seuls les liens aboutissaient. La règle serveur vit
 * dans `src/lib/profile-picture.ts`.
 */
interface ProfilePhotoDialogProps {
  open: boolean;
  /** Photo actuelle (aperçu tant qu'on n'en a pas choisi une autre). */
  currentPhoto?: string | null;
  /** Initiales affichées quand il n'y a aucune photo. */
  initials: string;
  onCancel: () => void;
  /**
   * Photo validée. Peut enregistrer immédiatement (fiche collaborateur) et renvoyer un
   * message d'erreur : la fenêtre reste alors ouverte et l'affiche.
   */
  onConfirm: (photo: string) => void | Promise<string | null | void>;
}

export function ProfilePhotoDialog({ open, currentPhoto, initials, onCancel, onConfirm }: ProfilePhotoDialogProps) {
  const { t } = useTranslation();
  const [tempPhoto, setTempPhoto] = useState<string | null>(null);
  const [photoUrlInput, setPhotoUrlInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setTempPhoto(null);
    setPhotoUrlInput("");
    setError(null);
    setBusy(false);
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset input to allow selecting the same file again
    e.target.value = "";
    if (!file) return;
    setError(null);
    setTempPhoto(await compressImageFile(file, { maxDim: PROFILE_PICTURE_MAX_DIM }));
  };

  const handleUrlSubmit = () => {
    if (photoUrlInput.trim()) {
      setError(null);
      setTempPhoto(photoUrlInput.trim());
      setPhotoUrlInput("");
    }
  };

  const handleConfirm = async () => {
    if (!tempPhoto) return;
    setBusy(true);
    setError(null);
    try {
      const result = await onConfirm(tempPhoto);
      if (typeof result === "string" && result) {
        setError(result);
        setBusy(false);
        return;
      }
      reset();
    } catch (err: any) {
      setError(err?.message || t("api.updateError"));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) handleCancel(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("account.changePhoto")}</DialogTitle>
          <DialogDescription>{t("account.changePhoto")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Aperçu */}
          <div className="flex justify-center">
            <div className="relative">
              <Avatar className="h-32 w-32">
                <AvatarImage src={tempPhoto || currentPhoto || undefined} />
                <AvatarFallback className="text-4xl">{initials}</AvatarFallback>
              </Avatar>
              {tempPhoto && (
                <Badge className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-green-500">
                  {t("account.changePhoto")}
                </Badge>
              )}
            </div>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 gap-2">
            {/* Upload depuis l'ordinateur */}
            <input type="file" ref={fileInputRef} accept="image/jpeg,image/png,image/webp,image/*" className="hidden" onChange={handleFileSelect} />
            <Button variant="outline" className="w-full justify-start" onClick={() => fileInputRef.current?.click()}>
              <Image className="h-4 w-4 mr-2" />
              {t("account.uploadFromDevice")}
            </Button>

            {/* Prendre une photo (mobile) */}
            <input type="file" ref={cameraInputRef} accept="image/*" capture="user" className="hidden" onChange={handleFileSelect} />
            <Button variant="outline" className="w-full justify-start" onClick={() => cameraInputRef.current?.click()}>
              <Camera className="h-4 w-4 mr-2" />
              {t("account.takePhoto")}
            </Button>

            {/* URL */}
            <Separator className="my-2" />
            <div className="space-y-2">
              <Label>{t("account.pasteUrl")}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="https://exemple.com/photo.jpg"
                  value={photoUrlInput}
                  onChange={(e) => setPhotoUrlInput(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleUrlSubmit} disabled={!photoUrlInput.trim()}>
                  <Link className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-[#C0423F]">{error}</p>}

          {/* Boutons de validation */}
          <Separator />
          <div className="flex gap-2 w-full">
            <Button onClick={handleConfirm} className="flex-1" disabled={!tempPhoto || busy}>
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
              {t("common.save")}
            </Button>
            <Button variant="outline" onClick={handleCancel} className="flex-1" disabled={busy}>
              <X className="h-4 w-4 mr-2" />
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
